/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { FlyDragEvent } from 'fly-drag-drop/utils/events';
import { tracked } from '@glimmer/tracking';
import { COLORS, TEXT_COLOR_MAP, META_TEXT_COLOR_MAP, NOTE_SIZE } from 'cio/utils/visual-workflow-builder/notes';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import AnalyticsService from 'cio/services/analytics';
import { COLOR_NAMES } from 'cio/utils/colors';
import { VWBContext } from 'cio/utils/visual-workflow-builder/context';
import CampaignNoteModel from 'cio/models/campaign-note';

interface VisualWorkflowBuilderV2StickyNotesNoteArgs {
  note: CampaignNoteModel;
  vwbContext: VWBContext;
}

export default class VisualWorkflowBuilderV2StickyNotesNote extends Component<VisualWorkflowBuilderV2StickyNotesNoteArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;

  @tracked x = 0;
  @tracked y = 0;
  width = NOTE_SIZE;
  height = NOTE_SIZE;
  @tracked content = '';
  @tracked color = '';

  @tracked isColorPickerOpen = false;
  @tracked isShowingDeleteModal = false;

  constructor(owner: unknown, args: VisualWorkflowBuilderV2StickyNotesNoteArgs) {
    super(owner, args);

    this.x = args.note.x;
    this.y = args.note.y;
    this.content = args.note.content;
    this.color = args.note.color;
  }

  get stickyNotesManager() {
    return this.args.vwbContext.stickyNotesManager;
  }

  get zoomManager() {
    return this.args.vwbContext.zoomManager;
  }

  willDestroy() {
    super.willDestroy();

    document.removeEventListener('mouseup', this.onDocumentMouseUp);
  }

  get campaign() {
    return this.stickyNotesManager.params.campaign;
  }

  get zoomLevel() {
    return this.zoomManager.zoomLevel;
  }

  colorOptions = COLORS.map((value) => ({ label: COLOR_NAMES[value], value }));

  get textColor() {
    return TEXT_COLOR_MAP[this.color];
  }

  get metaTextColor() {
    return META_TEXT_COLOR_MAP[this.color];
  }

  get readOnly() {
    let hasReadOnlyPermission = true;

    if (this.campaign.isApiTriggered === true) {
      hasReadOnlyPermission = !this.auth.hasPermission('journeys/broadcasts/edit');
    } else {
      hasReadOnlyPermission = !this.auth.hasPermission('journeys/campaigns/edit');
    }

    return hasReadOnlyPermission || !this.stickyNotesManager.canCreateOrEditNote;
  }

  startCoords: { x: number; y: number } = {
    x: 0,
    y: 0,
  };

  @tracked isEditing = false;
  @tracked isMouseDown = false;

  @action
  onClick() {
    if (this.readOnly) {
      return;
    }

    this.isEditing = !this.isEditing;

    if (this.isEditing) {
      this.analytics.track('campaign', 'edit_note_started', {
        model: this.campaign,
        note: this.args.note,
      });
    }
  }

  @action
  onMouseDown(e: MouseEvent) {
    // stop propagation so that the VWB's mousedown (selection) handlers don't
    // fire.
    e.stopPropagation();
    this.isMouseDown = true;

    document.addEventListener('mouseup', this.onDocumentMouseUp);
  }

  @action
  onDocumentMouseUp() {
    this.isMouseDown = false;
    this.isEditing = false;

    document.removeEventListener('mouseup', this.onDocumentMouseUp);
  }

  @action
  onDragStart(e: FlyDragEvent) {
    if (e.detail.clientX == null || e.detail.clientY == null) {
      return;
    }

    this.startCoords = {
      x: e.detail.clientX,
      y: e.detail.clientY,
    };

    this.analytics.track('campaign', 'move_note_started', {
      model: this.campaign,
      note: this.args.note,
    });
  }

  @action
  onDragEnd(e: FlyDragEvent) {
    if (e.detail.clientX == null || e.detail.clientY == null) {
      return;
    }

    let diffCoords = {
      x: e.detail.clientX - this.startCoords.x,
      y: e.detail.clientY - this.startCoords.y,
    };

    let scale = this.zoomLevel / 100;

    this.args.note.x = Math.round(this.x + diffCoords.x / scale);
    this.args.note.y = Math.round(this.y + diffCoords.y / scale);

    this.x = this.args.note.x;
    this.y = this.args.note.y;

    this.startCoords = { x: 0, y: 0 };

    this.args.note.save();

    this.analytics.track('campaign', 'move_note_completed', {
      model: this.campaign,
      note: this.args.note,
    });
  }

  updateContent = task({ restartable: true }, async () => {
    if (this.readOnly) {
      return;
    }

    if (this.args.note.content !== this.content) {
      this.args.note.content = this.content;
      this.args.note.save();

      this.analytics.track('campaign', 'edit_note_completed', {
        model: this.campaign,
        note: this.args.note,
      });
    }
  });

  changeColor = task({ restartable: true }, async (value: string, dropdown: BasicDropdown) => {
    if (this.readOnly) {
      return;
    }

    if (this.args.note.color !== value) {
      this.args.note.color = value;
      this.color = value;

      this.analytics.track('campaign', 'note_color_change_completed', {
        model: this.campaign,
        note: this.args.note,
        originalColor: this.color,
        newColor: this.args.note.color,
      });

      this.args.note.save();
    }

    dropdown.actions.close();
  });

  @action
  startDeleteNote(e: MouseEvent) {
    e.stopPropagation();

    this.isShowingDeleteModal = true;

    this.analytics.track('campaign', 'delete_note_started', {
      model: this.campaign,
      note: this.args.note,
    });
  }

  @action
  closeDeleteModal() {
    if (this.deleteNote.isRunning) {
      return;
    }

    this.isShowingDeleteModal = false;
  }

  deleteNote = task({ restartable: true }, async () => {
    this.analytics.track('campaign', 'delete_note_confirmed', {
      model: this.campaign,
      note: this.args.note,
    });

    await this.stickyNotesManager.deleteNote.perform(this.args.note.id);
    // no need to hide modal - this component will get destroyed after the task
    // is complete
  });

  @action
  onBlur(event: FocusEvent) {
    if ((event.target as HTMLTextAreaElement | null)?.value == null) {
      return;
    }

    this.content = (event.target as HTMLTextAreaElement).value;

    // the textarea loses focus as soon as mousedown is called. When isMouseDown
    // is true, it means we started a click within the note, but outside the
    // textarea. In this case we want the mousedown/mouseup handlers a couple of
    // lines above to handle resetting the isEditing state.
    if (!this.isMouseDown && this.isEditing) {
      this.isEditing = false;
    }
    this.updateContent.perform();
  }

  @action
  onTextAreaMouseDown(event: MouseEvent) {
    // if we're in edit mode, don't propagate the event, otherwise the drag
    // handlers will fire and it won't be possible to select text within the
    // textarea - the note would enter drag mode instead
    if (this.isEditing) {
      event.stopPropagation();
    }
  }

  @action
  onTextAreaClick(event: MouseEvent) {
    // similar to the above - if a click happens within the textarea, don't
    // propagate it so that the parent drag/click events don't fire
    // unnecessarily
    if (this.isEditing) {
      event.stopPropagation();
    }
  }

  @action
  onOpenColorPicker() {
    this.isColorPickerOpen = true;

    this.analytics.track('campaign', 'note_color_change_started', {
      model: this.campaign,
      note: this.args.note,
    });
  }
}
